import { Link } from 'react-router-dom';
import { LegalLayout } from './LegalLayout.js';
import { Clause, List, Mono, P, Plain, Terms } from './prose.js';

const EFFECTIVE = '2026-08-05';

/**
 * The privacy policy Meta requires as a URL on the app, written against what
 * this product actually does. Every claim here is checkable in the code: the
 * categories match the tables, the retention figures match the deletion path,
 * and the two things that survive an erasure are named rather than implied.
 */
export function PrivacyPage() {
  return (
    <LegalLayout
      label="Privacy"
      title="Privacy policy"
      effective={EFFECTIVE}
      standfirst={
        <>
          Legate finds businesses worth talking to and carries conversations inside the channels
          Meta permits. This document says exactly which records that produces, which it does not,
          how long each one lives and how to have yours removed.
        </>
      }
    >
      <Clause n={1} title="Who this is about">
        <P>
          Two different people read this page. The <strong className="text-ink font-medium">
          business</strong> that connects its Facebook Page or Instagram account to Legate, and the{' '}
          <strong className="text-ink font-medium">person</strong> who writes to that business and
          is answered by it.
        </P>
        <P>
          For everything a person sends to a connected account, the business is the controller and
          Legate is its processor: we hold the conversation because that business asked us to
          answer it, and we act on its instructions. For a customer&rsquo;s own account, billing and
          audit records, Legate is the controller.
        </P>
        <P>
          Legate is operated from the European Union. The operating entity&rsquo;s registration
          documents are held on file for Meta Business Verification and are provided on request to{' '}
          <Mono>legal@legate.dev</Mono>.
        </P>
      </Clause>

      <Clause n={2} title="What is collected">
        <P>Three categories, and nothing outside them.</P>
        <Terms
          items={[
            {
              term: 'Public business profiles',
              detail:
                'Trade name, website domain, country and city, industry, description, follower count, whether ads are running, posting cadence, average reply latency and whether chat automation is already in place. Read through the official Meta Graph API and from the business’s own public pages. This is a record about a company, not about a person.',
            },
            {
              term: 'Role-based contact points',
              detail:
                'Addresses a company publishes for itself — info@, sales@, a contact form, a switchboard number. A named individual’s work address is not collected, and a personal address never is.',
            },
            {
              term: 'Conversation content in connected channels',
              detail:
                'Messages exchanged in Messenger, Instagram Direct and WhatsApp with an account the customer connected through Meta’s own OAuth flow, plus the platform-scoped user id, the display name Meta supplies, the thread id, timestamps and the state of the 24-hour messaging window. Comments under the customer’s own posts and ads, where a comment-to-message trigger is configured.',
            },
            {
              term: 'Customer account records',
              detail:
                'For the business using Legate: work email address, password hash, wallet address if wallet sign-in is used, credit balance and ledger, and the audit trail of what its operators and the agent did.',
            },
          ]}
        />
      </Clause>

      <Clause n={3} title="What is not collected">
        <Plain>These are product decisions, not aspirations. Each is enforced in the code.</Plain>
        <List
          items={[
            <>
              <strong className="text-ink font-medium">Employee personal data.</strong> No scraped
              staff lists, no personal email addresses, no personal phone numbers, no CV or
              social-profile enrichment about individuals. The prospect graph stores role-based
              contacts only.
            </>,
            <>
              <strong className="text-ink font-medium">Passwords for Meta accounts.</strong> Access
              is granted through Meta&rsquo;s OAuth screen and stored as a token, encrypted with
              AES-256-GCM in fields separate from the rest of the row. Legate never asks for, and
              cannot use, a Meta login.
            </>,
            <>
              <strong className="text-ink font-medium">Messages outside authorised accounts.</strong>{' '}
              Only accounts a customer connected, only through the official API, only the
              conversations that account is party to. There is no reading of anyone else&rsquo;s
              inbox, and no automation driving a personal account.
            </>,
            <>
              <strong className="text-ink font-medium">Special categories and children.</strong> No
              health, biometric, political, religious or sexual-life data is sought or inferred, and
              the service is not directed at anyone under 18.
            </>,
            <>
              <strong className="text-ink font-medium">Cross-site tracking.</strong> One session
              cookie, <Mono>legate_session</Mono>, which keeps a signed-in operator signed in. No
              advertising cookies, no third-party analytics, no pixels on this site.
            </>,
          ]}
        />
      </Clause>

      <Clause n={4} title="The isolated console, stated plainly">
        <P>
          Legate offers an operator a browser session it hosts, used to complete Meta&rsquo;s own
          login and consent screens or to open Meta Business Suite. If an operator signs in there,
          the credentials go to Meta, never to us: the browser context, its cookie jar and its
          storage exist only in memory and are destroyed when the session ends. What survives is the
          session record — who opened it, which platform, when it closed — and its event log.
        </P>
        <P>
          The session is bound to the one operator who opened it. Another user of the same customer
          account cannot attach to it.
        </P>
      </Clause>

      <Clause n={5} title="Why each category is processed">
        <Terms
          items={[
            {
              term: 'Public business records',
              detail:
                'Legitimate interests, Art. 6(1)(f) GDPR: identifying companies that may want a service, in a business-to-business context, from information those companies publish about themselves. The balancing assessment is on file and available to a supervisory authority.',
            },
            {
              term: 'Conversations',
              detail:
                'Processed on the connected business’s documented instructions, Art. 28. That business relies on its own basis — normally the contract it is negotiating with the person, or its legitimate interest in answering someone who wrote to it first.',
            },
            {
              term: 'WhatsApp template messages',
              detail:
                'Consent, recorded before the first message with its source and the moment it was given. Without a live record in the opt-in registry the send is refused; consent withdrawn revokes the record.',
            },
            {
              term: 'Customer account and billing',
              detail:
                'Performance of the contract, Art. 6(1)(b), and legal obligations for invoicing and accounting.',
            },
            {
              term: 'Audit and security records',
              detail:
                'Legitimate interests and, for the audit trail, the platform obligation to be able to show what an automated agent did and why.',
            },
          ]}
        />
      </Clause>

      <Clause n={6} title="How long each record lives">
        <Terms
          items={[
            {
              term: 'Meta access tokens',
              detail:
                'Until the connection is revoked or the app is removed, then erased in the same operation — not marked unusable, erased.',
            },
            {
              term: 'Conversations and messages',
              detail:
                '24 months after the last message in the thread, unless the customer sets a shorter horizon or a deletion request arrives first.',
            },
            {
              term: 'Prospect graph records',
              detail: '24 months after the last observation, then removed with their signals.',
            },
            {
              term: 'Opt-out registry',
              detail:
                'Kept for as long as the customer uses Legate. This is the record that stops contact; see clause 8.',
            },
            {
              term: 'Audit trail',
              detail:
                '6 years. Append-only and immutable at the storage layer: what the agent did cannot be quietly rewritten afterwards.',
            },
            {
              term: 'Deletion requests',
              detail:
                '24 months, as the proof a request was honoured. The record holds a confirmation code, counts and timestamps — never the identifier that was erased.',
            },
            {
              term: 'Isolated console sessions',
              detail:
                'The session ends within its expiry, at most a few hours; the record of it and its events, 12 months.',
            },
          ]}
        />
      </Clause>

      <Clause n={7} title="Who else processes it">
        <P>
          By category, because a named vendor changes more often than a role does. Each is engaged
          under a written processing agreement, and none is permitted to use the data for its own
          purposes.
        </P>
        <Terms
          items={[
            {
              term: 'Cloud infrastructure',
              detail:
                'Compute, managed PostgreSQL and managed Redis, in an EU region. Data at rest is encrypted by the provider; access tokens are additionally encrypted by Legate before they reach the database.',
            },
            {
              term: 'Language-model routing',
              detail:
                'Drafting and classification are sent to a model gateway and the model providers it routes to. Only the conversation excerpt needed for the reply is sent, never the customer’s credentials or the prospect graph. Providers are configured not to train on the traffic. With no key configured, or a spent budget, the product falls back to a deterministic local engine and no message leaves at all.',
            },
            {
              term: 'Payment processing',
              detail:
                'Card payments are handled by a PCI-DSS Level 1 processor. Legate stores the transaction reference and amount; card numbers never reach our servers.',
            },
            {
              term: 'Email delivery',
              detail: 'Transactional messages — sign-in, receipts, deletion confirmations.',
            },
            {
              term: 'Meta Platforms',
              detail:
                'Not a sub-processor but the channel itself. Messages you send to a business travel through Meta under Meta’s own terms and privacy policy, whether or not Legate is involved.',
            },
          ]}
        />
        <P>
          Payments made in <Mono>$LEGATE</Mono> settle on a public blockchain. A transaction there
          is permanent and world-readable by design, and no one — including us — can delete it. A
          wallet address linked to an account can be unlinked; the chain record stays.
        </P>
      </Clause>

      <Clause n={8} title="The opt-out registry">
        <P>
          When someone asks a business not to contact them, that objection is recorded against their
          platform id and checked before every send. It is the shortest record in the system: an
          identifier, a reason, a date.
        </P>
        <Plain>
          It survives deletion, on purpose. Erasing the record of &ldquo;do not contact me&rdquo; is
          the one outcome an erasure must never produce, because it would quietly restore our
          ability to reach you.
        </Plain>
        <P>
          If you want that record gone as well, say so in writing to{' '}
          <Mono>privacy@legate.dev</Mono> and it will be removed — with the consequence stated back
          to you first.
        </P>
      </Clause>

      <Clause n={9} title="How data is protected">
        <List
          items={[
            'Meta access tokens are encrypted field-by-field with AES-256-GCM under a key that is not the database key, so a database dump does not yield a working token.',
            'Every query runs through a tenant-bound repository that injects the customer filter. There is no unfiltered query builder in the application code — one customer’s records cannot be read by another by mistake.',
            'Webhook deliveries are verified as HMAC-SHA256 over the raw request body before anything is read from them. Meta’s platform callbacks are verified the same way against the app secret.',
            'The audit trail is append-only and enforced as such by the database, not by convention.',
            'Personal data is not written to application logs. A request id links a log line to a request; the content stays out of it.',
          ]}
        />
      </Clause>

      <Clause n={10} title="Where it is processed">
        <P>
          Primary processing is in the European Union. Language-model providers and the payment
          processor may process outside the EEA; those transfers run on the European Commission&rsquo;s
          standard contractual clauses with the supplementary measures the transfer assessment
          requires.
        </P>
      </Clause>

      <Clause n={11} title="Your rights, and how to use them">
        <P>
          Access, rectification, erasure, restriction, objection, portability, and withdrawal of
          consent where consent is the basis. You may complain to your national supervisory
          authority at any time; we would rather you told us first.
        </P>
        <P>
          Deletion has a page of its own, with both routes — through Meta&rsquo;s own app settings,
          which reaches us automatically, and by writing to us:{' '}
          <Link
            to="/data-deletion"
            className="text-accent hover:text-accent-hover underline underline-offset-4 transition-colors duration-200"
          >
            how to delete your data
          </Link>
          .
        </P>
        <P>
          Any other request: <Mono>privacy@legate.dev</Mono>. Answered within 30 days. Where Legate
          is the processor and the request concerns a conversation with a particular business, we
          forward it to that business and act on their instruction — and we tell you that we have.
        </P>
      </Clause>

      <Clause n={12} title="Changes">
        <P>
          A material change is published here with a new date in force at least 14 days before it
          takes effect, and customers are notified by email. The version in force at the time of
          processing is the one that applies to it.
        </P>
        <P>
          Questions: <Mono>privacy@legate.dev</Mono>. Legate is built on the official Meta APIs and
          is not affiliated with, endorsed by, or a partner of Meta Platforms, Inc.
        </P>
      </Clause>
    </LegalLayout>
  );
}

export default PrivacyPage;
